import React from "react";
import { useNode, useEditor } from "@craftjs/core";
import {
  FormControl,
  FormLabel,
  TextField,
  Select as MuiSelect,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  ToggleButtonGroup,
  ToggleButton
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import {
  INPUT_TYPES,
  BULMA_COLORS,
  BULMA_SIZES,
  BULMA_STYLES,
  BULMA_STATES,
  FONT_FAMILIES
} from './constants/inputConstants';

export const InputSettings = () => {
  const {
    actions: { setProp },
    name, qwantumId, value, inputName, placeholder, type, readonly, disabled, allowEditing,
    bulmaSize, bulmaColor, bulmaStyle, bulmaState,
    hasLeftIcon, hasRightIcon, leftIconClass, rightIconClass,
    labelText, labelFontSize, showSemicolon, labelBold, labelItalic, fontFamily,
    width, maxWidth, minWidth, flex, flexGrow, flexShrink, flexBasis,
    position, top, left, right, bottom, unit, lockedEdges,
    backgroundColor, borderRadius, padding, cssClass, inputClass,
    parent
  } = useNode((node) => ({
    ...node.data.props
  }));

  const { query } = useEditor();
  const parentNode = parent ? query.node(parent).get() : null;

  const handleFlexChange = (prop, value) => {
    if (value === '') {
      setProp(props => props[prop] = undefined);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setProp(props => props[prop] = numValue);
      }
    }
  };

  return (
    <div>
      {parentNode && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Parent: {parentNode.data.displayName || parentNode.data.name || 'Container'}</FormLabel>
        </FormControl>
      )}

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Basic Properties</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Component Name</FormLabel>
            <TextField
              value={name || ''}
              onChange={(e) => setProp(props => props.name = e.target.value)}
              size="small"
              placeholder="Enter component name"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Input Name</FormLabel>
            <TextField
              value={inputName || ''}
              onChange={(e) => setProp(props => props.inputName = e.target.value)}
              size="small"
              placeholder="Enter input name attribute"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Quantum ID</FormLabel>
            <TextField
              value={qwantumId || ''}
              onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
              size="small"
              placeholder="Enter quantum ID for persistence"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Input Type</FormLabel>
            <MuiSelect
              value={type || 'text'}
              onChange={(e) => setProp(props => props.type = e.target.value)}
              size="small"
            >
              {INPUT_TYPES.map(inputType => (
                <MenuItem key={inputType.value} value={inputType.value}>{inputType.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Placeholder</FormLabel>
            <TextField
              value={placeholder || ''}
              onChange={(e) => setProp(props => props.placeholder = e.target.value)}
              size="small"
              placeholder="Enter placeholder text"
            />
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={readonly}
                onChange={(e) => setProp(props => props.readonly = e.target.checked)}
              />
            }
            label="Read only"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={disabled}
                onChange={(e) => setProp(props => props.disabled = e.target.checked)}
              />
            }
            label="Disabled"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={allowEditing}
                onChange={(e) => setProp(props => props.allowEditing = e.target.checked)}
              />
            }
            label="Allow Editing"
          />

          <FormControl fullWidth margin="normal">
            <FormLabel>CSS Class (Container)</FormLabel>
            <TextField
              value={cssClass || ''}
              onChange={(e) => setProp(props => props.cssClass = e.target.value)}
              size="small"
              placeholder="Enter CSS class names for container"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>CSS Class (Input)</FormLabel>
            <TextField
              value={inputClass || ''}
              onChange={(e) => setProp(props => props.inputClass = e.target.value)}
              size="small"
              placeholder="Enter CSS class names for input control"
            />
          </FormControl>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Bulma Styling</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Color</FormLabel>
            <MuiSelect
              value={bulmaColor || ''}
              onChange={(e) => setProp(props => props.bulmaColor = e.target.value)}
              size="small"
            >
              {BULMA_COLORS.map(color => (
                <MenuItem key={color.value} value={color.value}>{color.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Size</FormLabel>
            <MuiSelect
              value={bulmaSize || ''}
              onChange={(e) => setProp(props => props.bulmaSize = e.target.value)}
              size="small"
            >
              {BULMA_SIZES.map(size => (
                <MenuItem key={size.value} value={size.value}>{size.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Style</FormLabel>
            <MuiSelect
              value={bulmaStyle || ''}
              onChange={(e) => setProp(props => props.bulmaStyle = e.target.value)}
              size="small"
            >
              {BULMA_STYLES.map(style => (
                <MenuItem key={style.value} value={style.value}>{style.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>State</FormLabel>
            <MuiSelect
              value={bulmaState || ''}
              onChange={(e) => setProp(props => props.bulmaState = e.target.value)}
              size="small"
            >
              {BULMA_STATES.map(state => (
                <MenuItem key={state.value} value={state.value}>{state.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Icon Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Checkbox
                checked={hasLeftIcon}
                onChange={(e) => setProp(props => props.hasLeftIcon = e.target.checked)}
              />
            }
            label="Show left icon"
          />

          {hasLeftIcon && (
            <FormControl fullWidth margin="normal">
              <FormLabel>Left Icon Class</FormLabel>
              <TextField
                value={leftIconClass || ''}
                onChange={(e) => setProp(props => props.leftIconClass = e.target.value)}
                size="small"
                placeholder="e.g., fas fa-user"
              />
            </FormControl>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={hasRightIcon}
                onChange={(e) => setProp(props => props.hasRightIcon = e.target.checked)}
              />
            }
            label="Show right icon"
          />

          {hasRightIcon && (
            <FormControl fullWidth margin="normal">
              <FormLabel>Right Icon Class</FormLabel>
              <TextField
                value={rightIconClass || ''}
                onChange={(e) => setProp(props => props.rightIconClass = e.target.value)}
                size="small"
                placeholder="e.g., fas fa-check"
              />
            </FormControl>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Label Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Label Text</FormLabel>
            <TextField
              value={labelText || ''}
              onChange={(e) => setProp(props => props.labelText = e.target.value)}
              size="small"
              placeholder="Enter label text"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Label Font Size</FormLabel>
            <TextField
              value={labelFontSize || 14}
              onChange={(e) => setProp(props => props.labelFontSize = parseInt(e.target.value) || 14)}
              size="small"
              type="number"
              inputProps={{ min: 8, max: 72 }}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Font Family</FormLabel>
            <MuiSelect
              value={fontFamily || 'inherit'}
              onChange={(e) => setProp(props => props.fontFamily = e.target.value)}
              size="small"
            >
              {FONT_FAMILIES.map(font => (
                <MenuItem key={font.value} value={font.value}>{font.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={labelBold}
                onChange={(e) => setProp(props => props.labelBold = e.target.checked)}
              />
            }
            label="Bold label"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={labelItalic}
                onChange={(e) => setProp(props => props.labelItalic = e.target.checked)}
              />
            }
            label="Italic label"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={showSemicolon}
                onChange={(e) => setProp(props => props.showSemicolon = e.target.checked)}
              />
            }
            label="Show semicolon after label"
          />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Layout & Positioning</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Width</FormLabel>
            <TextField
              value={width || ''}
              onChange={(e) => setProp(props => props.width = e.target.value)}
              size="small"
              placeholder="100%, 300px, auto, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Max Width</FormLabel>
            <TextField
              value={maxWidth || ''}
              onChange={(e) => setProp(props => props.maxWidth = e.target.value)}
              size="small"
              placeholder="400px, 50%, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Min Width</FormLabel>
            <TextField
              value={minWidth || ''}
              onChange={(e) => setProp(props => props.minWidth = e.target.value)}
              size="small"
              placeholder="100px, 20%, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Position</FormLabel>
            <MuiSelect
              value={position || 'relative'}
              onChange={(e) => setProp(props => props.position = e.target.value)}
              size="small"
            >
              <MenuItem value="relative">Relative</MenuItem>
              <MenuItem value="absolute">Absolute</MenuItem>
            </MuiSelect>
          </FormControl>

          {(position === 'absolute' || position === 'relative') && (
            <>
              <FormControl fullWidth margin="normal">
                <FormLabel>Unit</FormLabel>
                <ToggleButtonGroup
                  value={unit}
                  exclusive
                  onChange={(e, value) => value && setProp(props => props.unit = value)}
                  size="small"
                >
                  <ToggleButton value="px">px</ToggleButton>
                  <ToggleButton value="%">%</ToggleButton>
                </ToggleButtonGroup>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <FormLabel>Position Controls</FormLabel>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lockedEdges?.top || false}
                        onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, top: e.target.checked })}
                      />
                    }
                    label="Top"
                  />
                  <TextField
                    value={top || ''}
                    onChange={(e) => setProp(props => props.top = parseFloat(e.target.value) || 0)}
                    size="small"
                    type="number"
                    disabled={!lockedEdges?.top}
                    style={{ width: '80px' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lockedEdges?.left || false}
                        onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, left: e.target.checked })}
                      />
                    }
                    label="Left"
                  />
                  <TextField
                    value={left || ''}
                    onChange={(e) => setProp(props => props.left = parseFloat(e.target.value) || 0)}
                    size="small"
                    type="number"
                    disabled={!lockedEdges?.left}
                    style={{ width: '80px' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lockedEdges?.right || false}
                        onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, right: e.target.checked })}
                      />
                    }
                    label="Right"
                  />
                  <TextField
                    value={right || ''}
                    onChange={(e) => setProp(props => props.right = parseFloat(e.target.value) || 0)}
                    size="small"
                    type="number"
                    disabled={!lockedEdges?.right}
                    style={{ width: '80px' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lockedEdges?.bottom || false}
                        onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, bottom: e.target.checked })}
                      />
                    }
                    label="Bottom"
                  />
                  <TextField
                    value={bottom || ''}
                    onChange={(e) => setProp(props => props.bottom = parseFloat(e.target.value) || 0)}
                    size="small"
                    type="number"
                    disabled={!lockedEdges?.bottom}
                    style={{ width: '80px' }}
                  />
                </div>
              </FormControl>
            </>
          )}

          <FormControl fullWidth margin="normal">
            <FormLabel>Flex</FormLabel>
            <TextField
              value={flex || ''}
              onChange={(e) => handleFlexChange('flex', e.target.value)}
              size="small"
              placeholder="1, 0 1 auto, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Flex Grow</FormLabel>
            <TextField
              value={flexGrow || ''}
              onChange={(e) => handleFlexChange('flexGrow', e.target.value)}
              size="small"
              placeholder="0, 1, 2, or leave empty"
              type="number"
              inputProps={{ min: 0, step: 1 }}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Flex Shrink</FormLabel>
            <TextField
              value={flexShrink || ''}
              onChange={(e) => handleFlexChange('flexShrink', e.target.value)}
              size="small"
              placeholder="1, 0, or leave empty"
              type="number"
              inputProps={{ min: 0, step: 1 }}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Flex Basis</FormLabel>
            <TextField
              value={flexBasis || ''}
              onChange={(e) => setProp(props => props.flexBasis = e.target.value || undefined)}
              size="small"
              placeholder="auto, 200px, 50%, or leave empty"
            />
          </FormControl>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Container Styling</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal" component="fieldset">
            <FormLabel component="legend">Background Color</FormLabel>
            <input
              type="color"
              value={backgroundColor || '#ffffff'}
              onChange={(e) => setProp(props => props.backgroundColor = e.target.value)}
            />
          </FormControl>

          <FormControl size="small" component="fieldset" fullWidth margin="normal">
            <FormLabel component="legend">Border Radius</FormLabel>
            <Slider
              value={borderRadius || 0}
              step={1}
              min={0}
              max={50}
              valueLabelDisplay="auto"
              onChange={(_, value) => {
                setProp(props => props.borderRadius = value);
              }}
            />
          </FormControl>

          <FormControl size="small" component="fieldset" fullWidth margin="normal">
            <FormLabel component="legend">Padding</FormLabel>
            <Slider
              value={padding || 0}
              step={1}
              min={0}
              max={50}
              valueLabelDisplay="auto"
              onChange={(_, value) => {
                setProp(props => props.padding = value);
              }}
            />
          </FormControl>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};